import React, { useCallback, useEffect, useState } from 'react';
import { OFFLINE_ICON } from './offlineIcon';

// Bottom banner shown whenever the device loses internet. It sits in the lower
// ~1/4 of the screen so the map/content above it stay visible - the rider can
// still see where they are while waiting to reconnect, instead of a full-
// screen blackout (matches the driver app's treatment of the same state).
//
// navigator.onLine + the online/offline events only fire on a TRANSITION, and
// navigator.onLine can report a stale `true` on a cold launch inside a Capacitor
// WebView - so on its own it misses "app opened with no network". We back it up
// with an active probe: a tiny HEAD request the network must actually satisfy.
// The `__ping` marker tells the service worker to skip this request entirely
// (see service-worker.js), so it goes straight to the real network - the SW
// can never falsely satisfy it from cache while offline.
const OfflineOverlay: React.FC = () => {
  const [offline, setOffline] = useState(!navigator.onLine);

  const probe = useCallback(async () => {
    try {
      await fetch(`/manifest.json?__ping=${Date.now()}`, { method: 'HEAD', cache: 'no-store' });
      setOffline(false);
    } catch {
      setOffline(true);
    }
  }, []);

  useEffect(() => {
    const goOnline = () => { setOffline(false); probe(); };
    const goOffline = () => setOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    probe();                                  // correct the initial guess (cold offline launch)
    const id = setInterval(probe, 12000);     // catch silent drops / recovery
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      clearInterval(id);
    };
  }, [probe]);

  if (!offline) return null;

  return (
    <div
      style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 99999,
        height: '25vh', minHeight: 150,
        background: '#000', borderTop: '1px solid rgba(245,166,35,0.35)',
        borderTopLeftRadius: 18, borderTopRightRadius: 18,
        boxShadow: '0 -8px 30px rgba(0,0,0,0.6)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '16px 24px calc(var(--sai-bottom) + 16px)', textAlign: 'center',
      }}
    >
      <style>{`@keyframes sc-off-beat { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.6); opacity: 0.5; } }`}</style>
      <img src={OFFLINE_ICON} alt="Offline" style={{ width: 52, height: 52, objectFit: 'contain', marginBottom: 10, opacity: 0.95 }} />
      <h2 style={{ color: '#fff', fontSize: 17, fontWeight: 700, margin: 0 }}>You lost your internet</h2>
      <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, marginTop: 6, maxWidth: 300 }}>
        Please reconnect to continue using SafariCab.
      </p>
      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, color: '#F5A623', fontSize: 12 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#F5A623', animation: 'sc-off-beat 1.2s ease-in-out infinite' }} />
        Waiting for connection…
      </div>
    </div>
  );
};

export default OfflineOverlay;
